import { BottomNavigation, BottomNavigationAction } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import Description from "@material-ui/icons/Description";
import Home from "@material-ui/icons/Home";
import SearchIcon from "@material-ui/icons/Search";
import StarIcon from "@material-ui/icons/Star";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { createSelector } from "reselect";
import VisibleFor from "../../auth/components/VisibleFor";
import { Role } from "../../auth/types";
import { IApplicationState } from "../../redux/root-reducer";
import { getAllDomainAlarms } from "../../safetySensors/Sensors.actions";
import { ISensorData } from "../../safetySensors/types";
import { Backdrop } from "./Backdrop";
import DynamicNotificationIcon from "./DynamicNotificationIcon";
import styles from "./Menu.module.scss";

export interface ICurrentAlarmsState {
  currentAlarms: ISensorData[] | undefined;
  currentUserRole: Role | undefined;
  isFetching: boolean;
}

const getCurrentAlarmsSelector = createSelector(
  (state: IApplicationState) => ({
    ...state.sensors,
    ...state.auth,
  }),
  (currentAlarms) => ({
    currentAlarms: currentAlarms.sensorsWithAlerts,
    currentUserRole: currentAlarms.currentUser?.role,
    isFetching: currentAlarms.isFetching,
  })
);
const getDomainSelector = createSelector(
  (state: IApplicationState) => state.common,
  (commonState) => ({
    domainCode: commonState.selectedDomain?.Code ?? "",
  })
);
const getTenantNodeIdSelector = createSelector(
  (state: IApplicationState) => state.common,
  (commonState) =>
    "NodeID" in commonState.currentUserInfo
      ? commonState.currentUserInfo.NodeID
      : undefined
);
const getCanViewBillingResultsSelector = createSelector(
  (state: IApplicationState) => state.common,
  (commonState) =>
    commonState.currentUserInfo.DomainSettings.TenantAccessStatus
      .canViewBillingResults
);

const Menu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const routeMatch = useRouteMatch<{ domain: string }>("/:domain/");
  const tenantNodeId = useSelector(getTenantNodeIdSelector);
  const canViewBillingResults = useSelector(getCanViewBillingResultsSelector);
  const backdropRef = React.useRef(null);
  const { domainCode } = useSelector(getDomainSelector);
  const { currentAlarms, currentUserRole, isFetching } = useSelector(
    getCurrentAlarmsSelector
  );

  useEffect(() => {
    if (domainCode && currentUserRole === Role.Landlord) {
      dispatch(getAllDomainAlarms(domainCode));
    }
  }, [domainCode]);

  return (
    <div className={styles.navigationContainer}>
      <Backdrop open={isFetching} innerRef={backdropRef} />
      <BottomNavigation showLabels className={styles.appBar}>
        <VisibleFor
          roles={[Role.Landlord]}
          child={
            <BottomNavigationAction
              label={t("Current alarms")}
              icon={
                <DynamicNotificationIcon notifications={currentAlarms.length} />
              }
              className={styles.alarmButton}
              onClick={() => history.push("/currentAlarms")}
            />
          }
        />
        <VisibleFor
          roles={[Role.Landlord]}
          child={
            <BottomNavigationAction
              label={t("Apartment")}
              icon={<SearchIcon />}
              onClick={() => history.push("/")}
            />
          }
        />
        <VisibleFor
          roles={[Role.Landlord]}
          child={
            <BottomNavigationAction
              label={t("Saved")}
              icon={<StarIcon />}
              onClick={() => history.push(`/favorites`)}
            />
          }
        />
        <VisibleFor
          roles={[Role.Tenant]}
          child={
            <BottomNavigationAction
              label={t("Home")}
              icon={<Home />}
              onClick={() =>
                history.push(
                  `/${routeMatch?.params.domain}/safetySensors/${tenantNodeId}`
                )
              }
            />
          }
        />
        {canViewBillingResults && (
          <VisibleFor
            roles={[Role.Tenant]}
            child={
              <BottomNavigationAction
                label={t("Invoice data")}
                icon={<Description />}
                onClick={() =>
                  history.push(
                    `/${
                      routeMatch?.params.domain
                    }${`/${tenantNodeId}`}/billing-results`
                  )
                }
              />
            }
          />
        )}
        <BottomNavigationAction
          label={t("Account")}
          icon={<PersonIcon />}
          onClick={() =>
            history.push(
              `${
                currentUserRole === Role.Tenant ? `/${domainCode}` : ""
              }/profile`
            )
          }
        />
      </BottomNavigation>
    </div>
  );
};

export default Menu;
